import {BulletList} from "@tiptap/extension-bullet-list";
import {OrderedList} from "@tiptap/extension-ordered-list";
import {Table} from "@tiptap/extension-table";
import {NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, useEditorState} from "@tiptap/react";
import {useEffect, useRef, useState} from "react";
import {Plugin, PluginKey} from 'prosemirror-state';
import Button from "../../Button/Button";

const withTextAlign = (extension) => extension.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            textAlign: {
                default: "left",
                parseHTML: el => el.style.textAlign || "left",
                renderHTML: ({textAlign}) => {
                    if (!textAlign || textAlign === "left") return {};
                    return {style: `text-align: ${textAlign}; list-style-position: inside;`};
                },
            },
        };
    },
});

export const CustomBulletList = withTextAlign(BulletList);

export const CustomOrderedList = withTextAlign(OrderedList).extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            listStyleType: {
                default: null,
                parseHTML: el => el.style.listStyleType || null,
                renderHTML: ({listStyleType}) => {
                    if (!listStyleType) return {}
                    return {style: `list-style-type: ${listStyleType}`}
                },
            },
        }
    },
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('orderedListDepth'),
                appendTransaction(transactions, oldState, newState) {
                    const docChanged = transactions.some(tr => tr.docChanged)
                    if (!docChanged) return null

                    const tr = newState.tr
                    let changed = false

                    newState.doc.descendants((node, pos) => {
                        if (node.type.name !== 'orderedList') return

                        const $pos = newState.doc.resolve(pos)
                        let depth = 0
                        for (let i = $pos.depth; i > 0; i--) {
                            if ($pos.node(i).type.name === 'orderedList') depth++
                        }

                        const listStyleType = depth % 2 === 0 ? 'decimal' : 'lower-alpha'

                        if (node.attrs.listStyleType !== listStyleType) {
                            tr.setNodeMarkup(pos, undefined, {
                                ...node.attrs,
                                listStyleType,
                            })
                            changed = true
                        }
                    })

                    return changed ? tr : null
                }
            })
        ]
    }
})

export const CustomTable = Table.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            selected: {
                default: false,
                renderHTML: () => {
                }
            }
        }
    },
    addNodeView() {
        return ReactNodeViewRenderer(CustomTableView)
    },
    addProseMirrorPlugins() {
        return (this.parent?.() ?? []).filter(plugin =>
            !plugin.spec?.props?.handleDOMEvents?.mousemove
        )
    }
})

const CustomTableView = ({node, editor}) => {
    const [open, setOpen] = useState(false);
    const modalRef = useRef(null);
    const buttonRef = useRef(null);
    const isEditable = useEditorState({
        editor,
        selector: (state) => state.editor.isEditable,
    });

    const handleAddRow = (e) => {
        e.preventDefault();
        editor.chain().focus().addRowAfter().run();
        setOpen(false);
    }

    const handleAddColumn = (e) => {
        e.preventDefault();
        editor.chain().focus().addColumnAfter().run();
        setOpen(false);
    }

    const handleDeleteRow = (e) => {
        e.preventDefault();
        editor.chain().focus().deleteRow().run();
        setOpen(false);
    }

    const handleDeleteColumn = (e) => {
        e.preventDefault();
        editor.chain().focus().deleteColumn().run();
        setOpen(false);
    }

    useEffect(() => {
        const handler = (e) => {
            if (!modalRef.current?.contains(e.target) && !buttonRef.current?.contains(e.target)) {
                setOpen(false);
            }
        }

        document.addEventListener("mousedown", handler);

        return () => document.removeEventListener("mousedown", handler);
    }, [])

    return (
        <NodeViewWrapper>
            <div className="table-wrapper">
                <div style={{position: "relative", width: "fit-content"}}>
                    {isEditable && (
                        <>
                            <Button
                                ref={buttonRef}
                                className="table-actions"
                                onMouseDown={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setOpen(prev => !prev)
                                }}
                            >
                                +
                            </Button>

                            {open && (
                                <div ref={modalRef} className="table-actions-modal">
                                    <Button onMouseDown={handleAddColumn}>
                                        + Column
                                    </Button>
                                    <Button onMouseDown={handleAddRow}>
                                        + Row
                                    </Button>
                                    <Button onMouseDown={handleDeleteColumn}>
                                        - Column
                                    </Button>
                                    <Button onMouseDown={handleDeleteRow}>
                                        - Row
                                    </Button>
                                </div>
                            )}
                        </>
                    )}

                    <NodeViewContent as="table" className={node.attrs.class}/>
                </div>
            </div>
        </NodeViewWrapper>
    )
}